import { SubmitErrorHandler, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useEffect, useMemo } from 'react'
import { CurrencyCode, CurrencySymbol } from 'Utils/types/currencies'
import { isNonZeroAmount, parseAmount } from 'Utils/helpers/currency'
import { formatDateForServer, sameMonth } from 'Utils/helpers/dateUtils'
import { toast } from 'react-toastify'
import { useAddUserRecurringAllowanceMutation } from 'Hooks/api/JOONAdmin/useAddUserRecurringAllowanceMutation'
import { useUpdateUserRecurringAllowanceMutation } from 'Hooks/api/JOONAdmin/useUpdateUserRecurringAllowanceMutation'
import { User } from 'Utils/types/user'
import { BenefitProgram } from 'Utils/types/benefitProgram'
import { RecurringAllowance } from 'Utils/types/allowance'

export type RecurringAllowanceFormValues = {
  allowanceType: string
  amount: string
  startDate: Date
  cadence: string
  duration: string
  active: boolean
  notes: string
}

type UseRecurringAllowanceFormArgs = {
  currencyCode: CurrencyCode
  userId: User['id']
  benefitProgramId: BenefitProgram['id']
  editingRecurringAllowance?: RecurringAllowance | null
  onEditComplete?: () => void
}

const defaultValues: Partial<RecurringAllowanceFormValues> = {
  allowanceType: 'birthday',
  cadence: 'yearly',
  duration: 'end_of_year',
  active: true,
  notes: ''
}

export const useRecurringAllowanceForm = ({
  currencyCode,
  userId,
  benefitProgramId,
  editingRecurringAllowance,
  onEditComplete
}: UseRecurringAllowanceFormArgs) => {
  const isEditMode = !!editingRecurringAllowance
  const currencySymbol = useMemo(() => CurrencySymbol[currencyCode], [currencyCode])

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors, isValid }
  } = useForm<RecurringAllowanceFormValues>({
    mode: 'onChange',
    defaultValues
  })

  const { isLoading: isAdding, mutateAsync: addMutateAsync } =
    useAddUserRecurringAllowanceMutation()
  const { isLoading: isUpdating, mutateAsync: updateMutateAsync } =
    useUpdateUserRecurringAllowanceMutation()

  const isLoading = isAdding || isUpdating

  const watchedStartDate = useWatch({ control, name: 'startDate' })
  const isStartDateInCurrentMonth = useMemo(() => {
    if (!watchedStartDate || isEditMode) return false
    return sameMonth(watchedStartDate, new Date())
  }, [watchedStartDate, isEditMode])

  useEffect(() => {
    if (editingRecurringAllowance) {
      setValue('allowanceType', editingRecurringAllowance.allowanceType, { shouldValidate: true })
      setValue('amount', `${currencySymbol} ${editingRecurringAllowance.amount}`, {
        shouldValidate: true
      })
      setValue('startDate', new Date(editingRecurringAllowance.startDate), { shouldValidate: true })
      setValue('cadence', editingRecurringAllowance.cadence, { shouldValidate: true })
      setValue('duration', editingRecurringAllowance.duration, { shouldValidate: true })
      setValue('active', editingRecurringAllowance.active, { shouldValidate: true })
      setValue('notes', editingRecurringAllowance.notes || '', { shouldValidate: true })
    } else {
      reset({
        ...defaultValues,
        amount: `${currencySymbol} `,
        startDate: undefined
      })
    }
  }, [editingRecurringAllowance, setValue, currencySymbol, reset])

  const registerOptions = {
    allowanceType: {
      required: 'An allowance type is required.'
    },
    cadence: {
      required: 'A cadence is required.'
    },
    duration: {
      required: 'A duration is required.'
    },
    startDate: {
      required: 'A start date for the recurring allowance is required.'
    },
    notes: {},
    amount: {
      validate: (value: unknown) =>
        isNonZeroAmount(value) || 'A valid, non-zero amount is required.'
    }
  }

  const handleSubmission: SubmitHandler<RecurringAllowanceFormValues> = async ({
    allowanceType,
    amount,
    startDate,
    cadence,
    duration,
    active,
    notes
  }) => {
    const basePayload = {
      allowanceType,
      amount: `${parseAmount(amount)}`,
      startDate: formatDateForServer(startDate),
      cadence,
      duration,
      active,
      notes: notes.trim() || undefined
    }

    const response = isEditMode
      ? await updateMutateAsync({ id: editingRecurringAllowance!.id, ...basePayload })
      : await addMutateAsync({ userId, benefitProgramId, ...basePayload })

    if (response.success) {
      if (isEditMode) {
        onEditComplete?.()
      } else {
        reset({
          ...defaultValues,
          amount: `${currencySymbol} `,
          startDate: undefined
        })
      }
    }
  }

  const handleError: SubmitErrorHandler<RecurringAllowanceFormValues> = (errors) => {
    if (errors.allowanceType?.message) toast.error(errors.allowanceType.message)
    if (errors.amount?.message) toast.error(errors.amount.message)
    if (errors.startDate?.message) toast.error(errors.startDate.message)
    if (errors.notes?.message) toast.error(errors.notes.message)
  }

  return {
    register,
    handleSubmit,
    control,
    setValue,
    errors,
    registerOptions,
    currencySymbol,
    isLoading,
    handleError,
    handleSubmission,
    isValid,
    isStartDateInCurrentMonth
  }
}
